import { produce } from 'immer';
import { atom, selector } from 'recoil';

import {
  checkEveryOptionChecked,
  getSelectedParentChildFilters,
  toggleCheckboxFilters,
} from '@/services/used-cars/used-cars.service';
import {
  ChildFilter,
  ParentFilter,
  SearchFilter,
} from '@/types/filters/filters.types';
import { guardRecoilDefaultValue } from '@/utils/atom';

export const brandsRangesState = atom<ParentFilter[]>({
  key: 'brandsAndRanges',
  default: [],
});
export const headerBrandState = atom<ParentFilter[]>({
  key: 'headerBrands',
  default: [],
});
export const allBrandState = atom<ParentFilter[]>({
  key: 'allBrands',
  default: [],
});

export const brandsRangesSearchFiltersSelector = selector({
  key: 'brandsAndRangesSearch',
  get: ({ get }) => {
    const brandsAndRanges = get(brandsRangesState);

    const brandsRangesSearchFilters: ChildFilter[] = brandsAndRanges.reduce(
      (acc, brand) => {
        const childrenWithAlias =
          brand?.children?.map((child) => ({
            ...child,
            alias: brand.alias,
          })) || [];

        return [...acc, brand, ...childrenWithAlias];
      },
      [] as ChildFilter[],
    );

    return { brandsRangesSearchFilters };
  },
});

export const brandsRangesSelectedFiltersSelector = selector({
  key: 'brandsRangesSelectedFilters',
  get: ({ get }) => {
    const brandsAndRanges = get(brandsRangesState);
    const brandsRangesSelectedFilters: SearchFilter[] =
      getSelectedParentChildFilters(brandsAndRanges);

    return { brandsRangesSelectedFilters };
  },
});

export const brandsRangesSearchOptionsSelector = selector({
  key: 'brandsRangesSearchOptionsSelector',
  get: ({ get }) => {
    const { brandsRangesSearchFilters } = get(
      brandsRangesSearchFiltersSelector,
    );
    const brandsRangesSearchOptions: SearchFilter[] =
      brandsRangesSearchFilters.filter((option) => !option.isChecked);

    return { brandsRangesSearchOptions };
  },
});

export const brandsRangesAllSelectedSelector = selector({
  key: 'brandsRangesAllSelected',
  get: ({ get }) => {
    const brandsRanges = get(brandsRangesState);
    const brandsRangesAllSelected: boolean =
      checkEveryOptionChecked(brandsRanges);

    return { brandsRangesAllSelected };
  },
});

export const hasSelectedBrandsRangesSelector = selector<boolean>({
  key: 'hasSelectedBrandsRanges',
  get({ get }) {
    const { brandsRangesSelectedFilters } = get(
      brandsRangesSelectedFiltersSelector,
    );

    return Boolean(brandsRangesSelectedFilters?.length);
  },
});

export const selectBrandSelector = selector({
  key: 'selectBrand',
  get: () => [],
  set: ({ set, get }, brandIds) => {
    const brandRanges = get(brandsRangesState);

    const updatedBrandRanges = produce(brandRanges, (brandRangesDraft) => {
      if (guardRecoilDefaultValue(brandIds)) return;

      if (brandIds?.length) {
        brandIds
          .map((brandId) => Number(brandId))
          .forEach((brandId) => {
            const brand = brandRangesDraft.find(
              (brand) => brand.id === (brandId as unknown as string),
            );

            if (!brand) return;

            brand.isChecked = true;
          });

        return;
      }
    });

    set(brandsRangesState, updatedBrandRanges);
  },
});

export const toggleBrandRangeSelector = selector<ParentFilter | ChildFilter>({
  key: 'toggleBrandRange',
  get: () => <ParentFilter | ChildFilter>{},
  set: ({ set, get }, brandRange) => {
    const brandRanges = get(brandsRangesState);

    const updatedBrandRanges = toggleCheckboxFilters(
      brandRanges,
      brandRange as ParentFilter | ChildFilter,
    );

    set(brandsRangesState, updatedBrandRanges);
  },
});

export const selectRangeSelector = selector({
  key: 'selectRange',
  get: () => [],
  set: ({ set, get }, rangeIds) => {
    const brandRanges = get(brandsRangesState);

    const updatedBrandRanges = produce(brandRanges, (brandRangesDraft) => {
      if (guardRecoilDefaultValue(rangeIds)) return;
      if (rangeIds?.length) {
        rangeIds
          .map((rangeId) => Number(rangeId))
          .forEach((rangeId) => {
            brandRangesDraft.forEach((brand) => {
              const range = brand.children?.find(
                (range) => range.id === `${brand.id}-${rangeId}`,
              );

              if (!range) return;

              range.isChecked = true;
            });
          });

        return;
      }
    });

    set(brandsRangesState, updatedBrandRanges);
  },
});

export const resetBrandsRangesSelector = selector({
  key: 'resetBrandsRanges',
  get: () => null,
  set: ({ set, get }) => {
    const brandsAndRanges = get(brandsRangesState);
    const updatedBrandsRanges = produce(
      brandsAndRanges,
      (draftBrandsAndRanges) => {
        draftBrandsAndRanges.forEach((brand) => {
          brand.isChecked = false;

          brand.children?.forEach((range) => {
            range.isChecked = false;
          });
        });
      },
    );

    set(brandsRangesState, updatedBrandsRanges);
  },
});
